import { computed, inject, Injector, Service } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import type {
  OnyxConfirmModalData,
  OnyxDropdownOptionsSource,
  OnyxFilter,
  OnyxHttpErrorResponse,
  OnyxMaybeArray,
  OnyxOption,
  OnyxOptional,
  OnyxOptionsGroup,
  OnyxPagination,
} from '@onyx/angular';
import {
  ActionHelper,
  AddressHelper,
  DropdownHelper,
  OnyxConfirmModalComponent,
  OnyxFilterHelper,
  OnyxFilterOperator,
  OnyxModalService,
  OnyxToastService,
} from '@onyx/angular';
import { castArray, chain, isArray, isString, orderBy } from 'lodash';
import type { Observable, Subject } from 'rxjs';
import { catchError, filter, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { AuthStore } from '../../../../auth/common/stores/auth.store';
import { VALIDATION_ERROR_CODES } from '../../../../common/constants/common/validation-error-codes';
import { ColorHelper } from '../../../../common/helpers/color.helper';
import { CommonHelper } from '../../../../common/helpers/common.helper';
import { NavigationHelper } from '../../../../common/helpers/navigation.helper';
import type { ValidationContext } from '../../../../common/helpers/validation-conditions.helper';
import { ValidationConditionsHelper } from '../../../../common/helpers/validation-conditions.helper';
import type { TelematicsItem } from '../../../../common/interfaces/telematics/telematics-item';
import type { HelperOptions } from '../../../../common/interfaces/utilities/helper-options';
import { FleetIdentifierPipe } from '../../../../common/pipes/fleet-identifier.pipe';
import { TelematicsStore } from '../../../../common/stores/telematics.store';
import { XlsxService } from '../../../common/services/xlsx.service';
import { DelegationHelper } from '../../../delegations/common/helpers/delegation.helper';
import { TelematicsHelper } from '../../../map/common/helpers/telematics.helper';
import type { FleetModalData } from '../../fleet-modal/fleet-modal.component';
import {
  FleetModalComponent,
  FleetModalSection,
} from '../../fleet-modal/fleet-modal.component';
import type { FleetAssignedDriversModalData } from '../components/fleet-assigned-drivers-modal/fleet-assigned-drivers-modal.component';
import { FleetAssignedDriversModalComponent } from '../components/fleet-assigned-drivers-modal/fleet-assigned-drivers-modal.component';
import type { FleetAssignedEmployeesModalData } from '../components/fleet-assigned-employees-modal/fleet-assigned-employees-modal.component';
import { FleetAssignedEmployeesModalComponent } from '../components/fleet-assigned-employees-modal/fleet-assigned-employees-modal.component';
import type { FleetSetsModalData } from '../components/fleet-sets-modal/fleet-sets-modal.component';
import { FleetSetsModalComponent } from '../components/fleet-sets-modal/fleet-sets-modal.component';
import { FLEET_EXPORT_DECIMAL_PLACES } from '../constants/fleet-export-decimal-places';
import { getFleetExportData } from '../constants/fleet-export-schema';
import { FLEET_XLSX_TEMPLATE } from '../constants/fleet-xlsx-template';
import { TRAILER_CATEGORIES } from '../constants/trailer-categories';
import { VEHICLE_CATEGORIES } from '../constants/vehicle-categories';
import { FleetCategory } from '../enums/fleet-category';
import { FleetRoute } from '../enums/fleet-route';
import { FleetRouterStateKey } from '../enums/fleet-router-state-key';
import { FleetState } from '../enums/fleet-state';
import type { Fleet, SimplifiedFleet } from '../interfaces/fleet';
import { FleetService } from '../services/fleet.service';
import type { SimplifiedDelegation } from './../../../delegations/common/interfaces/delegation';
import { FleetTypeHelper } from './fleet-type.helper';
import { FleetValidationHelper } from './fleet-validation.helper';

export const FLEET_HEADER_ACTIONS = [
  'share',
  'showOnGantt',
  'showOnMap',
] as const;

@Service()
export class FleetHelper {
  private readonly fleetService = inject(FleetService);
  private readonly authStore = inject(AuthStore);
  private readonly fleetIdentifierPipe = inject(FleetIdentifierPipe);
  private readonly fleetValidationHelper = inject(FleetValidationHelper);
  private readonly validationConditionsHelper = inject(
    ValidationConditionsHelper,
  );
  private readonly modalService = inject(OnyxModalService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly toastService = inject(OnyxToastService);
  private readonly injector = inject(Injector);
  private readonly xlsxService = inject(XlsxService);

  private readonly I18N = 'fleet.fleetList';

  public hasModule = computed(() => this.authStore.hasModule('fleet'));

  public getFleetSource(
    params?: Omit<
      Parameters<FleetService['searchFleet']>[1],
      keyof OnyxPagination
    >,
    warnings?: {
      enableWarnings: boolean;
      context: ValidationContext;
    },
  ): OnyxDropdownOptionsSource<Fleet> {
    return {
      list: (query, pagination) =>
        this.fleetService
          .searchFleet(query, {
            ...pagination,
            ...params,
            filters: OnyxFilterHelper.merge(
              [FleetHelper.createStateFilter(FleetState.ACTIVE)],
              params?.filters,
            ),
          })
          .pipe(
            map((result) => {
              if (warnings?.enableWarnings || !warnings?.context) return result;

              return {
                ...result,
                options: chain(DropdownHelper.mapToOptions(result.options)!)
                  .map((option) => {
                    return this.fleetValidationHelper.validateFleet({
                      context: warnings.context,
                      primary: option.value,
                      options: {
                        option,
                      },
                    }).option!;
                  })
                  .thru((options) =>
                    this.validationConditionsHelper.groupOptions(options),
                  )
                  .value(),
              };
            }),
          ),
      get: (uuid) =>
        this.fleetService.getFleet(uuid).pipe(
          map((vehicle) => ({
            name: this.fleetIdentifierPipe.transform(vehicle),
            value: vehicle,
          })),
        ),
      idKey: 'uuid',
    };
  }

  public getVehiclesSource(
    params?: Omit<
      Parameters<FleetService['searchFleet']>[1],
      keyof OnyxPagination
    >,
  ): OnyxDropdownOptionsSource<Fleet> {
    return {
      list: (query, pagination) =>
        this.fleetService.searchFleet(query, {
          ...pagination,
          ...params,
          filters: OnyxFilterHelper.merge(
            [FleetHelper.createCategoryFilter([...VEHICLE_CATEGORIES])],
            params?.filters,
          ),
        }),
      get: (uuid) =>
        this.fleetService.getFleet(uuid).pipe(
          map((vehicle) => ({
            name: this.fleetIdentifierPipe.transform(vehicle),
            value: vehicle,
          })),
        ),
      idKey: 'uuid',
    };
  }

  public getTrailersSource(): OnyxDropdownOptionsSource<Fleet> {
    return {
      list: (query, pagination) =>
        this.fleetService.searchFleet(query, {
          ...pagination,
          filters: [FleetHelper.createCategoryFilter([...TRAILER_CATEGORIES])],
        }),
      get: (uuid) =>
        this.fleetService.getFleet(uuid).pipe(
          map((trailer) => ({
            name: this.fleetIdentifierPipe.transform(trailer),
            value: trailer,
          })),
        ),
      idKey: 'uuid',
    };
  }

  public canPlanChange(
    item: Fleet | TelematicsItem,
    delegation: SimplifiedDelegation | null,
  ): boolean {
    return (
      FleetTypeHelper.isVehicle(item) && DelegationHelper.isActive(delegation)
    );
  }

  public getCrewOptions(options: {
    fleet?: Fleet;
    telematicsItem: OnyxOptional<TelematicsItem>;
    delegation: SimplifiedDelegation | null;
  }): OnyxOption<() => void>[] {
    if (!this.hasModule()) return [];

    if (options?.fleet?.uuid && !options.telematicsItem) {
      options.telematicsItem = this.injector
        .get(TelematicsStore)
        .unsortedData()
        .get(options.fleet.uuid);
    }

    const { fleet, telematicsItem, delegation } = options ?? {};
    const hasActiveDelegation = DelegationHelper.isActive(delegation);

    const item = fleet ?? telematicsItem;
    if (!item) return [];

    const telematicsStore = this.injector.get(TelematicsStore);
    const isVehicle = FleetTypeHelper.isVehicle(item);
    const spinner = !FleetHelper.isFleetType(item) && telematicsStore.loading();

    return [
      ...(isVehicle || !hasActiveDelegation
        ? [
            {
              name: this.canPlanChange(item, delegation)
                ? `${this.translateService.instant('labels.planChange')}...`
                : `${this.translateService.instant('labels.changeSet')}...`,
              value: () => this.changeVehicleSet(item, { delegation }),
              leftIcon: { name: 'exchange-filled', size: 20 },
              ...(item &&
                TelematicsHelper.hasSwap(item, telematicsStore) &&
                DropdownHelper.getErrorFields('issues.swapExists')),
              spinner,
              ...(spinner && { disabled: true }),
            } as const,
          ]
        : []),
      ...(isVehicle
        ? [
            {
              name: `${this.I18N}.changeDrivers`,
              value: () =>
                fleet
                  ? this.openAssignedDriversModal(fleet)
                  : this.fleetService
                      .getFleet(item.uuid)
                      .subscribe((fleet) =>
                        this.openAssignedDriversModal(fleet),
                      ),
              leftIcon: { name: 'steering-wheel-filled', size: 20 },
              ...(hasActiveDelegation &&
                DropdownHelper.getErrorFields(
                  'labels.vehicleHasActiveDelegation',
                )),
            } as const,
            {
              name: `${this.I18N}.changeEmployees`,
              value: () =>
                fleet
                  ? this.openAssignedEmployeesModal(fleet)
                  : this.fleetService
                      .getFleet(item.uuid)
                      .subscribe((fleet) =>
                        this.openAssignedEmployeesModal(fleet),
                      ),
              leftIcon: { name: 'group-outlined', size: 20 },
            } as const,
          ]
        : []),
    ];
  }

  public getOptions(
    fleet: Fleet,
    options: HelperOptions,
    exclude: readonly (typeof FLEET_HEADER_ACTIONS)[number][] = [],
  ): OnyxOptionsGroup<() => void>[] {
    const telematicsItem = this.injector
      .get(TelematicsStore)
      .unsortedData()
      .get(fleet.uuid);

    const isArchived = FleetHelper.isArchived(fleet);
    const isVehicle = FleetTypeHelper.isVehicle(fleet);
    const hasActiveDelegation = DelegationHelper.isActive(fleet.delegation);

    return [
      {
        id: '0.default',
        options: [
          {
            name: `${this.translateService.instant('labels.edit')}...`,
            value: () => this.edit(fleet.uuid, options),
            leftIcon: { name: 'edit-outlined', size: 20 },
            ...(!!fleet.relatedFleetSwap &&
              DropdownHelper.getErrorFields(
                'labels.disabledUntilSwapCompleted',
              )),
          } as const,
          {
            name: `${this.translateService.instant('labels.duplicate')}...`,
            value: () => this.duplicateFleet(fleet, options),
            leftIcon: { name: 'copy-outlined', size: 20 },
          } as const,
        ],
      },
      {
        id: '1.manage',
        options: [
          ...(isVehicle
            ? [
                {
                  name: `${this.translateService.instant('map.routeCalculator.valuateOrder')}...`,
                  value: () =>
                    CommonHelper.showOnMap(
                      this.router,
                      fleet.uuid,
                      this.injector,
                      { showRouteCalculator: true },
                    ),
                  leftIcon: { name: 'calculate-outlined', size: 20 },
                  disabled: !fleet.telemetry,
                  tooltip: !fleet.telemetry
                    ? 'labels.noTelematicsData'
                    : undefined,
                } as const,
              ]
            : []),
          {
            name: `${this.translateService.instant('labels.scheduleService')}...`,
            value: () =>
              CommonHelper.showOnMap(this.router, fleet.uuid, this.injector, {
                showService: true,
              }),
            leftIcon: { name: 'service-outlined', size: 20 },
          } as const,
        ],
      },
      ...(!isArchived
        ? [
            {
              id: '2.actions',
              options: [
                ...(!exclude.includes('share')
                  ? [
                      {
                        name: 'labels.share',
                        value: () => this.share(fleet),
                        leftIcon: { name: 'share-outlined', size: 20 },
                      } as const,
                    ]
                  : []),
                ...(!exclude.includes('showOnMap')
                  ? [
                      {
                        name: 'labels.showOnMap',
                        value: () =>
                          CommonHelper.showOnMap(
                            this.router,
                            fleet.uuid,
                            this.injector,
                          ),
                        leftIcon: { name: 'map-view-outlined', size: 20 },
                      } as const,
                    ]
                  : []),
                ...(!exclude.includes('showOnGantt')
                  ? [
                      {
                        name: 'labels.showOnGantt',
                        value: () =>
                          CommonHelper.showOnGantt(
                            this.router,
                            'vehicle',
                            fleet.uuid,
                          ),
                        leftIcon: { name: 'gantt-outlined', size: 20 },
                      } as const,
                    ]
                  : []),
                ...(fleet.telemetry
                  ? [
                      {
                        name: 'labels.copyLocation',
                        value: () => {
                          const coordinates =
                            AddressHelper.composeCoordinatesLabel(
                              fleet.telemetry!,
                            );
                          ActionHelper.copy(coordinates, this.toastService);
                        },
                        leftIcon: { name: 'location-outlined', size: 20 },
                      } as const,
                    ]
                  : []),
              ],
            },
          ]
        : []),
      {
        id: '3.crew',
        options: this.getCrewOptions({
          fleet,
          telematicsItem,
          delegation: fleet.delegation,
        }),
      },
      {
        id: '4.archive',
        options: [
          {
            name: 'labels.export',
            value: () => this.export(fleet),
            leftIcon: { name: 'export-outlined', size: 20 },
          } as const,
          {
            name: isArchived ? 'labels.unarchive' : 'labels.archive',
            value: () => this.toggleArchived(fleet, options),
            leftIcon: {
              name: isArchived ? 'undo-outlined' : 'archives-outlined',
              size: 20,
            },
            leftIconColor: isArchived ? 'black' : 'red',
            ...(hasActiveDelegation &&
              DropdownHelper.getErrorFields(
                `${this.I18N}.cannotArchiveVehicleWithActiveDelegation`,
              )),
            ...(!!fleet.orderUuid &&
              DropdownHelper.getErrorFields(
                `${this.I18N}.cannotArchiveVehicleWithAssignedOrder`,
              )),
          } as const,
        ],
      },
    ].filter(({ options }) => !!options.length);
  }

  public openModal(data: FleetModalData | null): void {
    if (data && this.hasModule()) {
      this.modalService.open<FleetModalData>(FleetModalComponent, data);
    }
  }

  public share(fleet: SimplifiedFleet): void {
    ActionHelper.share(
      FleetRoute.FLEET_CARD.replace(':uuid', fleet.uuid),
      this.toastService,
    );
  }

  public openAssignedDriversModal(data: FleetAssignedDriversModalData): void {
    if (!this.hasModule()) return;

    this.modalService.open<FleetAssignedDriversModalData>(
      FleetAssignedDriversModalComponent,
      data,
    );
  }

  public changeVehicleSet(
    data: FleetSetsModalData,
    options?: {
      delegation: SimplifiedDelegation | null;
      close$?: Subject<void>;
    },
  ): void {
    if (!this.hasModule()) return;

    const { delegation, close$ } = options ?? {};

    if (
      FleetTypeHelper.isVehicle(data) &&
      DelegationHelper.isActive(delegation)
    ) {
      CommonHelper.showOnMap(this.router, data.uuid, this.injector, {
        showSwapPanel: true,
      });
      close$?.next();

      return;
    }

    this.modalService.open<FleetSetsModalData>(FleetSetsModalComponent, data);
  }

  public edit(uuid: string, options: HelperOptions): void {
    this.router.navigateByUrl(
      FleetRoute.EDIT_FLEET.replace(':uuid', uuid),
      NavigationHelper.withOrigin(this.router),
    );
    CommonHelper.handleOptions(uuid, options);
  }

  public export(data: OnyxMaybeArray<Fleet>): void {
    this.xlsxService.export(
      {
        template: FLEET_XLSX_TEMPLATE,
        name: !isArray(data)
          ? `${this.fleetIdentifierPipe.transform(data)}`
          : `dashboard.navBar.routes.${FleetRoute.FLEET_LIST}`,
      },
      getFleetExportData(data, this.injector),
      FLEET_EXPORT_DECIMAL_PLACES,
    );
  }

  public openAssignedEmployeesModal(
    fleet: OnyxMaybeArray<FleetAssignedEmployeesModalData[number]>,
  ): void {
    if (!this.hasModule()) return;

    const vehicles = castArray(fleet).filter((item) =>
      FleetTypeHelper.isVehicle(item),
    );
    if (!vehicles.length) return;

    this.modalService.open<FleetAssignedEmployeesModalData>(
      FleetAssignedEmployeesModalComponent,
      vehicles,
    );
  }

  public toggleArchived(
    fleet: OnyxMaybeArray<Fleet>,
    options: HelperOptions,
    force?: boolean,
  ): void {
    const items = chain(fleet)
      .thru((fleet) => castArray(fleet))
      .groupBy((fleet) => force ?? !FleetHelper.isArchived(fleet))
      .thru((groups) => ({
        archive: groups['true'] ?? [],
        unarchive: groups['false'] ?? [],
      }))
      .value();
    const showConfirmModal = items.archive.some(
      (fleet) =>
        fleet.trailer ||
        fleet.vehicle ||
        fleet.assignedEmployees?.length ||
        fleet.drivers,
    );

    const toggle$ = (items: Fleet[], force: boolean): Observable<void> => {
      if (!items.length) return of(undefined);

      return this.fleetService
        .batchFleet(
          items.map((item) => item.uuid),
          { state: force ? FleetState.ARCHIVED : FleetState.ACTIVE },
        )
        .pipe(
          catchError((response: OnyxHttpErrorResponse) => {
            if (
              VALIDATION_ERROR_CODES.includes(response.status) &&
              response.error.includes(
                'Vehicle assigned to unfinished delegations cannot be archived',
              )
            ) {
              this.toastService.showError({
                message: 'fleet.toasts.cannotArchiveVehicle',
                description: 'fleet.toasts.cannotArchiveVehicleDescription',
              });
              response.handled = true;
            }

            throw response;
          }),
          tap(() =>
            this.toastService.showSuccess({
              message: this.translateService.instant(
                `fleet.toasts.${items.length === 1 ? 'vehicle' : 'vehicles'}${force ? 'Archived' : 'Unarchived'}`,
                { count: items.length },
              ),
            }),
          ),
        );
    };

    const toggleAll$ = (groups: typeof items) =>
      forkJoin([
        toggle$(groups.archive, true),
        toggle$(groups.unarchive, false),
      ]);

    of(items)
      .pipe(
        switchMap((items) => {
          if (showConfirmModal) {
            return this.modalService
              .open<OnyxConfirmModalData, boolean>(OnyxConfirmModalComponent, {
                heading: this.translateService.instant(
                  `${this.I18N}.sureToArchive${items.archive.length === 1 ? 'Vehicle' : 'Vehicles'}`,
                  {
                    vehicle: this.fleetIdentifierPipe.transform(
                      items.archive[0],
                    ),
                  },
                ),
                badges:
                  items.archive.length > 1
                    ? items.archive.map((fleet) => ({
                        value: this.fleetIdentifierPipe.transform(fleet),
                        color: 'outlined-black',
                      }))
                    : undefined,
                message: `${this.I18N}.sureToContinueArchive`,
                confirmButtonText: 'labels.archive',
                action: () => toggleAll$(items),
              })
              .result.pipe(filter((ok) => !!ok));
          }
          return toggleAll$(items);
        }),
      )
      .subscribe(() => CommonHelper.handleOptions(fleet, options));
  }

  public duplicateFleet(fleet: Fleet, options?: HelperOptions): void {
    this.router.navigateByUrl(
      FleetRoute.ADD_FLEET,
      NavigationHelper.withOrigin(this.router, {
        state: {
          [FleetRouterStateKey.CATEGORY]: fleet.generalInformation.category,
          [FleetRouterStateKey.TYPE]: fleet.generalInformation.type,
          [FleetRouterStateKey.TEMPLATE]: fleet,
        },
      }),
    );
    options && CommonHelper.handleOptions(fleet.uuid, options);
  }

  private delete(fleet: Fleet, options: HelperOptions): void {
    this.modalService
      .open<OnyxConfirmModalData, boolean>(OnyxConfirmModalComponent, {
        heading: this.translateService.instant('fleet.delete.heading', {
          fleet: this.fleetIdentifierPipe.transform(fleet),
        }),
        message: 'fleet.delete.message',
        confirmButtonText: 'labels.delete',
        confirmButtonColor: 'red',
        action: () => this.fleetService.deleteFleet(fleet),
      })
      .result.subscribe((ok) => {
        if (!ok) return;

        this.toastService.showSuccess({
          message: `fleet.toasts.delete.${fleet.generalInformation.category}`,
        });
        CommonHelper.handleOptions(fleet, options);
      });
  }

  public static getVehicleExpiryDocuments(
    vehicle: Pick<
      Fleet,
      | 'registrationCertificate'
      | 'comprehensiveInsurance'
      | 'thirdPartyLiabilityInsurance'
      | 'tachograph'
    >,
  ) {
    return [
      {
        key: 'technicalInspection',
        section: FleetModalSection.DOCUMENTS,
        expiryDate:
          vehicle.registrationCertificate.technicalInspectionExpiryDate,
      },
      {
        key: 'comprehensiveInsurance',
        section: FleetModalSection.DOCUMENTS,
        expiryDate: vehicle.comprehensiveInsurance?.expiryDate,
      },
      {
        key: 'thirdPartyLiabilityInsurance',
        section: FleetModalSection.DOCUMENTS,
        expiryDate: vehicle.thirdPartyLiabilityInsurance?.expiryDate,
      },
      {
        key: 'tachographLegalization',
        section: FleetModalSection.DETAILS,
        expiryDate: vehicle.tachograph?.nextLegalizationDate,
      },
    ];
  }

  public static getVehicleExpiryStatuses(vehicle: Fleet) {
    const statuses = orderBy(
      FleetHelper.getVehicleExpiryDocuments(vehicle),
      (status) => status.expiryDate,
    )
      .map((status) => {
        const expiryColor = status.expiryDate
          ? ColorHelper.getCellColor(status.expiryDate)
          : null;
        return expiryColor ? { ...status, color: expiryColor } : null;
      })
      .filter((status) => status != null);

    return {
      errors: statuses.filter((status) => status.color === 'red'),
      warnings: statuses.filter((status) => status.color === 'yellow'),
    };
  }

  public static getTrailerLabel(vehicle: OnyxOptional<Fleet>): string {
    if (!vehicle) return 'labels.semiTrailerOrTrailer';

    switch (vehicle?.generalInformation.category) {
      case FleetCategory.SEMI_TRUCK:
        return 'labels.semiTrailer';

      default:
        return 'labels.trailer';
    }
  }

  public static isArchived(
    fleet: Fleet | SimplifiedFleet | FleetState,
  ): boolean {
    const state = isString(fleet) ? fleet : fleet.generalInformation.state;
    return state === FleetState.ARCHIVED;
  }

  public static isFleetType(
    vehicle: Fleet | SimplifiedFleet | TelematicsItem,
  ): vehicle is Fleet {
    return 'ownership' in vehicle;
  }

  public static hasTelematics(fleet: Fleet | SimplifiedFleet | null) {
    if (!fleet) return false;
    return fleet.telematics != null;
  }

  public static createStateFilter(state: FleetState): OnyxFilter {
    return { key: 'state', operator: OnyxFilterOperator.EQ, value: state };
  }

  public static createCategoryFilter(categories: FleetCategory[]): OnyxFilter {
    return {
      key: 'category',
      operator: OnyxFilterOperator.IN,
      value: categories,
    };
  }
}
